import { useState } from "react";
import { Link } from "react-router-dom";
import { comparisonData } from "@/mocks/healthInsuranceCompanies";
import { useBrochureManager } from "@/hooks/useBrochureManager";

export default function ComparisonSection() {
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [showComparison, setShowComparison] = useState(false);
  const { logDownload } = useBrochureManager();

  const togglePlan = (planKey: string) => {
    setSelectedPlans((prev) => {
      if (prev.includes(planKey)) {
        return prev.filter((p) => p !== planKey);
      }
      if (prev.length >= 4) {
        return prev;
      }
      return [...prev, planKey];
    });
  };

  const selectedRows = comparisonData.filter((row) =>
    selectedPlans.includes(`${row.company}-${row.plan}`)
  );

  const handlePrint = () => {
    window.print();
  };

  return (
    <section className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-8 md:mb-10">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold border border-primary-100 mb-3">
            <i className="ri-file-list-3-line"></i>
            Side-by-Side Comparison
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
            Health Insurance Plan Comparison
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto text-sm md:text-base">
            Select up to 4 plans below to compare features, benefits, and find the best coverage for your needs.
          </p>
        </div>

        {/* Plan Selector */}
        <div className="mb-8">
          <p className="text-sm font-medium text-slate-700 mb-3">
            Select plans to compare ({selectedPlans.length}/4):
          </p>
          <div className="flex flex-wrap gap-2">
            {comparisonData.map((row) => {
              const key = `${row.company}-${row.plan}`;
              const isSelected = selectedPlans.includes(key);
              return (
                <button
                  key={key}
                  onClick={() => togglePlan(key)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer border ${
                    isSelected
                      ? "bg-primary-50 border-primary-300 text-primary-700 shadow-sm"
                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                      isSelected
                        ? "bg-primary-500 border-primary-500"
                        : "border-slate-300"
                    }`}
                  >
                    {isSelected && <i className="ri-check-line text-white text-xs"></i>}
                  </div>
                  <span className="whitespace-nowrap">
                    {row.plan}
                  </span>
                  <span className="text-xs text-slate-400 whitespace-nowrap">
                    ({row.company.split(" ")[0]})
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Comparison Table */}
        {selectedPlans.length > 0 && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <p className="text-sm text-slate-500">
                Comparing{" "}
                <span className="font-semibold text-slate-700">
                  {selectedPlans.length}
                </span>{" "}
                plan{selectedPlans.length > 1 ? "s" : ""}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowComparison(!showComparison)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium transition-colors cursor-pointer"
                >
                  <i
                    className={`ri-${showComparison ? "arrow-up-s-line" : "arrow-down-s-line"}`}
                  ></i>
                  {showComparison ? "Collapse Table" : "Expand Table"}
                </button>
                <button
                  onClick={handlePrint}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors shadow-sm cursor-pointer"
                >
                  <i className="ri-printer-line"></i>
                  Print / Save PDF
                </button>
              </div>
            </div>

            <div
              className={`overflow-hidden transition-all duration-500 ${
                showComparison ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div className="overflow-x-auto rounded-xl border border-slate-200 print:border print:border-slate-300">
                <table className="w-full min-w-[700px] text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 w-48">
                        Feature
                      </th>
                      {selectedRows.map((row) => (
                        <th
                          key={`${row.company}-${row.plan}`}
                          className="text-left px-4 py-3 font-semibold text-slate-700 min-w-[200px]"
                        >
                          <div className="text-primary-700 font-bold">
                            {row.plan}
                          </div>
                          <div className="text-xs text-slate-500 font-normal mt-0.5">
                            {row.company}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr>
                      <td className="px-4 py-3 font-medium text-slate-600 bg-slate-50/50">
                        Best Suited For
                      </td>
                      {selectedRows.map((row) => (
                        <td key={row.plan} className="px-4 py-3 text-slate-600">
                          {row.bestSuitedFor}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-slate-600 bg-slate-50/50 align-top">
                        Key Benefits
                      </td>
                      {selectedRows.map((row) => (
                        <td key={row.plan} className="px-4 py-3 text-slate-600 align-top">
                          <ul className="space-y-1.5">
                            {row.keyBenefits.map((benefit, idx) => (
                              <li key={idx} className="flex items-start gap-1.5">
                                <i className="ri-check-line text-cta-500 shrink-0 mt-0.5"></i>
                                <span>{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-slate-600 bg-slate-50/50">
                        Special Notes
                      </td>
                      {selectedRows.map((row) => (
                        <td key={row.plan} className="px-4 py-3 text-slate-600">
                          {row.specialNotes ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-medium">
                              <i className="ri-information-line"></i>
                              {row.specialNotes}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-slate-600 bg-slate-50/50">
                        Actions
                      </td>
                      {selectedRows.map((row) => (
                        <td key={row.plan} className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <a
                              href={row.brochureUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() =>
                                logDownload(
                                  `${row.company} — ${row.plan}`,
                                  "brochure",
                                  row.brochureUrl
                                )
                              }
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary-50 text-primary-700 text-xs font-medium hover:bg-primary-100 transition-colors"
                            >
                              <i className="ri-download-line"></i>
                              Brochure
                            </a>
                            <Link
                              to="/book-appointment?source=Health%20Insurers%20%26%20Brochures%20Page"
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-cta-50 text-cta-700 text-xs font-medium hover:bg-cta-100 transition-colors"
                            >
                              <i className="ri-phone-line"></i>
                              Advisor
                            </Link>
                          </div>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}